import { Brand } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-store';
import type { Menu } from '@ps/web-martech-cms-types';
import classNames from 'clsx';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useStickyTopHeaderClassName } from '@swf/cms';
import StarsNavigationItem from '@swf/components/StarsNavigation/StarsNavigationItem';
import SearchIcon from '@swf/core/assets/icons/icon-search.svg';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';

import useCustomHeader from '../../hooks/useCustomHeader';
import CMSMenuGraphProvider from '../CMSMenu/CMSMenuGraphProvider';

import styles from './CMSNavigation.scss';
import CMSNavigationItem from './CMSNavigationItem';
import CMSNavigationSubmenu from './CMSNavigationSubmenu';
import DesktopPlayMoneyToggle from './components/DesktopPlayMoneyToggle/DesktopPlayMoneyToggle';
import PlayMoneyToggle from './components/PlayMoneyToggle/PlayMoneyToggle';
import Search from './components/Search/Search';

interface Props {
  menu: Menu;
  maxDepth: number;
}

const CMSNavigation: React.FC<Props> = ({ menu, maxDepth }) => {
  const [open, setOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState<number>();
  const [searchOpen, setSearchOpen] = useState(false);
  const { children: submenu } = menu;
  const data = useSelector(select.cms.getContent);
  const { lightHeader, prospectHeader } = useCustomHeader();
  const withTag = submenu.some((child) => !!(data[child.uid] as Menu)?.tag_text);
  const playMoney = useFeature(FeatureFlag.PlayMoney);
  const isDesktopEmbedded = useSelector(select.device.isDesktopEmbedded);
  const isMobile = useSelector(select.device.isMobile);
  const stickyTopHeaderClassName = useStickyTopHeaderClassName('casino');
  const brandId = useSelector(select.cms.getBrandId);

  const openSubmenu = useCallback((index: number) => {
    setOpen(true);
    setOpenIndex(index);
  }, []);

  const closeSubmenu = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggle = useCallback(() => setSearchOpen(!searchOpen), [searchOpen]);

  return !lightHeader && !prospectHeader && submenu.length ? (
    <div
      className={classNames(styles.container, stickyTopHeaderClassName, {
        [styles.searchOpen]: searchOpen,
        [styles.desktop]: isDesktopEmbedded,
        [styles.mobile]: isMobile,
        [styles.sticky]: brandId === Brand.PokerStarsCasino
      })}
    >
      <div
        className={classNames(styles.backgroundWrapper, {
          [styles.withTag]: withTag,
          [styles.casino]: brandId === Brand.PokerStarsCasino
        })}
      >
        <nav className={styles.nav}>
          <CMSMenuGraphProvider menu={menu}>
            <ul
              className={classNames(styles.navList, { [styles.withTag]: withTag })}
              onMouseLeave={() => closeSubmenu()}
            >
              {submenu.map((item, index) => (
                <React.Fragment key={item.uid}>
                  <li
                    className={styles.topLevel}
                    onTouchEnd={() => openSubmenu(index)}
                    onMouseOver={() => openSubmenu(index)}
                  >
                    <CMSNavigationItem reference={item} maxDepth={maxDepth} />
                  </li>
                  <li className={styles.subNav}>
                    {open && openIndex === index && maxDepth > 1 && (
                      <CMSNavigationSubmenu menuItemRef={item} onSelect={closeSubmenu} />
                    )}
                  </li>
                </React.Fragment>
              ))}
            </ul>
          </CMSMenuGraphProvider>
          {brandId === Brand.PokerStarsCasino && (
            <div className={styles.right}>
              {(isDesktopEmbedded || isMobile) && (
                <StarsNavigationItem
                  tagName="button"
                  className={classNames(styles.searchIcon, styles.item, {
                    [styles.mobile]: isMobile
                  })}
                  active={searchOpen}
                  onClick={handleToggle}
                >
                  <SearchIcon />
                </StarsNavigationItem>
              )}
              {playMoney && (
                <section className={styles.item}>
                  {isDesktopEmbedded ? <DesktopPlayMoneyToggle /> : <PlayMoneyToggle />}
                </section>
              )}
              {((!isDesktopEmbedded && !isMobile) || searchOpen) && (
                <Search
                  className={classNames(styles.item, styles.search)}
                  onToggle={handleToggle}
                />
              )}
            </div>
          )}
        </nav>
      </div>
    </div>
  ) : null;
};

export default CMSNavigation;
